import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { DialogAction, DialogActionProps } from 'components/_new/Dialog';
import { Dropdown } from 'primereact/dropdown';
import { Controller, useForm } from 'react-hook-form';
import ValidateTextArea from 'components/_common/forms/TextArea';
import { Application } from 'types/Application';
import { ExpandablePanel } from 'components/_new/ExpandablePanel';
import { Avatar } from 'components/_new/Avatar';
import { Link } from 'react-router-dom';
import { useProgramPanel } from 'contexts/programPanelContext';
import { userDisplayName } from 'utils/userDisplayName';

type ChangeApplicationsStatusDialogProps = {
	applicationList: Application[];
	onConfirm: (data: { status: string; reason?: string; withNotification: boolean }) => void;
	loading?: boolean;
} & Omit<DialogActionProps, 'actions' | 'title'>;

export const ChangeApplicationsStatusDialog = ({
	applicationList,
	onConfirm,
	loading,
	...props
}: ChangeApplicationsStatusDialogProps) => {
	const { t } = useTranslation();
	const { data: program } = useProgramPanel();

	const { control, watch, handleSubmit } = useForm({
		defaultValues: {
			status: 'accepted',
			reason: '',
		},
	});

	const status = watch('status');

	const statusOptions = [
		{ label: t('programPanel.application.statusOptions.notAccepted'), value: 'notAccepted' },
		{ label: t('programPanel.application.statusOptions.accepted'), value: 'accepted' },
		{ label: t('programPanel.application.statusOptions.rejected'), value: 'rejected' },
	];

	const actions = [
		{
			key: 'confirmWithNotification',
			label: status === 'accepted' ? t('actions.acceptWithNotification') : t('actions.rejectWithNotification'),
			onClick: handleSubmit((data) => onConfirm({ ...data, withNotification: true })),
			loading,
			variant: 'primary' as any,
		},
		{
			key: 'confirmWithoutNotification',
			label:
				status === 'accepted' ? t('actions.acceptWithoutNotification') : t('actions.rejectWithoutNotification'),
			onClick: handleSubmit((data) => onConfirm({ ...data, withNotification: false })),
			loading,
			variant: 'grey' as any,
		},
	];

	return (
		<DialogAction title={t('actions.changeStatus')} actions={actions} {...props}>
			<div className="flex flex-column gap-4">
				{applicationList.length > 0 && (
					<ExpandablePanel
						header={
							<div>
								<Trans
									t={t}
									i18nKey="programPanel.pairing.removeApplication.applicationsToProcessInfo"
									components={[<span className="text-purple bold" />]}
									count={applicationList.length}
								/>
							</div>
						}
						expandedHeaderClassName="text-purple font-bold"
						contentClassName="grid mt-2"
						className="mb-4"
					>
						{applicationList.map((application) => (
							<Link
								key={application.id}
								target="_blank"
								to={`/program/${program?.name}/application/${application.id}`}
								className="col-12 md:col-6 flex flex-row gap-2 align-items-center mb-2"
							>
								<Avatar
									src={application.programMembership.user.avatar}
									size="md"
									name={userDisplayName(application.programMembership.user, null)}
								/>
								<span>{userDisplayName(application.programMembership.user)}</span>
							</Link>
						))}
					</ExpandablePanel>
				)}

				<div className="flex flex-column gap-2">
					<label htmlFor="status" className="font-bold">
						{t('programPanel.dashboard.status.title')}
					</label>
					<Controller
						name="status"
						control={control}
						render={({ field }) => (
							<Dropdown
								id="status"
								options={statusOptions}
								value={field.value}
								onChange={(e) => field.onChange(e.value)}
								className="w-full"
							/>
						)}
					/>
				</div>

				{status === 'rejected' && (
					<div className="flex flex-column gap-2 mt-2">
						<label htmlFor="reason" className="font-bold">
							{t('programPanel.application.enterRejectReason')}
						</label>
						<ValidateTextArea
							control={control}
							name="reason"
							rows={5}
							placeholder={t('programPanel.pairing.rejectDialog.placeholder')}
						/>
					</div>
				)}
			</div>
		</DialogAction>
	);
};
